import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import '../styles/globals.css';

const SuccessPage = () => {
  const [percentage, setPercentage] = useState(0);
  const [referralLink, setReferralLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [position, setPosition] = useState(0);
  const [maxPosition, setMaxPosition] = useState(100);
  const [showTransition, setShowTransition] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Get referral code and email from sessionStorage
    const referralCode = sessionStorage.getItem('referralCode');
    const email = sessionStorage.getItem('email');
    const apiKey = sessionStorage.getItem('apiKey');
    
    // Redirect to landing page if required data is missing
    if (!email || !apiKey) {
      navigate('/', { replace: true });
      return;
    }

    if (referralCode) {
      // Construct the full referral link with the base URL
      const baseUrl = window.location.origin;
      setReferralLink(`${baseUrl}/?ref=${referralCode}`);
    }

    // Hide transition after a delay
    setTimeout(() => {
      setShowTransition(false);
    }, 500);

    // If we have position from navigation state, use it
    if (location.state?.position && location.state?.maxTarget) {
      setPosition(location.state.position);
      setMaxPosition(location.state.maxTarget);
      calculateAndAnimatePercentage(location.state.position, location.state.maxTarget);
      return;
    }

    // Otherwise fetch from API
    if (email) {
      console.log('Fetching user stats...');
      fetch(`${process.env.REACT_APP_API_URL}/api/stats/total-users?email=${encodeURIComponent(email)}`, {
        headers: {
          'X-API-Key': apiKey
        }
      })
        .then(response => {
          console.log('Stats fetch status:', response.status);
          return response.json();
        })
        .then(data => {
          console.log('Stats data:', data);
          if (data.success) {
            setPosition(data.position);
            setMaxPosition(data.maxTarget);
            calculateAndAnimatePercentage(data.position, data.maxTarget);
          } else {
            throw new Error(data.error || 'Failed to fetch stats');
          }
        })
        .catch(error => {
          console.error('Failed to fetch user stats:', error);
          // If there's an authentication error, redirect to landing page
          if (error.message.includes('Unauthorized') || error.message.includes('Invalid API key')) {
            navigate('/', { replace: true });
            return;
          }
          setPosition(1);
          setMaxPosition(100);
          setPercentage(1);
        });
    }
  }, [location.state, navigate]);

  const calculateAndAnimatePercentage = (position, target) => {
    // Calculate percentage based on position and target
    const calculatedPercentage = Math.max(1, Math.min(99, (position / target) * 100));
    
    // Animate percentage
    let start = 0;
    const end = Math.round(calculatedPercentage);
    const duration = 2000;
    const startTime = performance.now();

    const updatePercentage = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const currentPercentage = Math.round(progress * end);
      
      setPercentage(currentPercentage);

      if (progress < 1) {
        requestAnimationFrame(updatePercentage);
      }
    };

    requestAnimationFrame(updatePercentage);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Create an array of 48 rods (360° / 7.5° = 48 rods)
  const rods = Array.from({ length: 48 }, (_, index) => {
    const angle = (index * 7.5) - 90; // Start from top (-90°)
    const isActive = (index / 48) * 100 <= percentage;
    
    return (
      <div
        key={index}
        className={`rod ${isActive ? 'active' : ''}`}
        style={{
          transform: `rotate(${angle}deg)`,
        }}
      />
    );
  });

  return (
    <div className="landing-container">
      <PageTransition isActive={showTransition} />
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 20, 20, 0.99)',
          zIndex: 0
        }}
      />
      <img 
        src="/background.jpg" 
        alt="Background" 
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: -1
        }}
      />

      <div style={{ 
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: '600px'
      }}>
        <div className="percentage-display">
          <div className="circular-rods">
            {rods}
          </div>
        </div>

        <p style={{ 
          fontSize: '1.25rem', 
          marginBottom: '2rem',
          color: '#fff',
          textAlign: 'center'
        }}>
          You are {position} out of {maxPosition} to join the Plasmafia Movement.
        </p>

        <div className="referral-link-container">
          <input
            type="text"
            className="referral-link"
            value={referralLink}
            readOnly
          />
          <button
            className="submit-button"
            onClick={handleCopyLink}
            style={{ minWidth: '100px' }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage; 